import { logger } from 'common/services';
import i18n from 'i18n';
import { addAppAlertDialog, addAppConfirmationDialog, addAppErrorMessage } from 'modules/alerts/state';
import { type ApiErrors, isApiErrors } from 'modules/api/utils';

import { insightsReportsWizardActions } from '../../../pages/InsightsReportsWizard/state/insightsReportsWizard.actions';

import type { Effect } from 'store';

const isRegeneratedReport = (payload: unknown) =>
  typeof payload === 'object' && payload !== null && (payload as { regenerated?: unknown }).regenerated === true;

export const createInsightsReportsSucceededEffect: Effect = ({ payload }, { dispatch }) => {
  dispatch(insightsReportsWizardActions.exit({ shouldGoBack: false }));
  // A regeneration sends no email, so it must not promise one.
  if (isRegeneratedReport(payload)) {
    dispatch(
      addAppAlertDialog({
        title: i18n.t('insightReport:wizard.reportRegenerating.title'),
        description: i18n.t('insightReport:wizard.reportRegenerating.description'),
        confirmLabel: i18n.t('insightReport:wizard.reportRegenerating.confirm'),
      })
    );
    return;
  }
  dispatch(
    addAppAlertDialog({
      title: 'We Are Creating Your Report',
      description: `We’ll notify you via email as soon as it's ready!`,
      confirmLabel: 'Got it',
    })
  );
};

const isReportAlreadyExistsError = ({ errors }: ApiErrors) =>
  errors.some((error) => new RegExp(/report for .* already exists/i).test(error.message));

export const createInsightsReportsRejectedEffect: Effect = ({ payload }, { dispatch }) => {
  if (!isApiErrors(payload.data)) return;

  if (isReportAlreadyExistsError(payload.data)) {
    dispatch(
      addAppConfirmationDialog({
        severity: 'danger',
        title: 'Creation unsuccessful',
        description: `You already have created a Report from the selected sources. Therefore you can’t have a duplicate. If you want to create a new one you are required to delete the existing one.`,
        confirmLabel: 'Got it!',
        cancelLabel: 'Close',
        showCancelButton: true,
      })
    );
    return;
  }

  logger.error('Insights report creation was refused', { context: 'createInsightsReports' }, payload);
  const { errors } = payload.data as ApiErrors;
  errors.forEach(({ message }) => {
    dispatch(addAppErrorMessage(message));
  });
};
